import {deleteRequest, get, post} from "./http.client";
import {apiBindings} from "./constants";
import {MapDocument, MapManifestEntry, sanitizeMapDocument} from "../models/map.model";

// Thin HTTP layer over the Maps endpoints (Remote/Maps.cs). All calls ride the
// shared session auth in http.client; reads are anonymous on the backend.

export async function fetchMapList(): Promise<MapManifestEntry[] | undefined> {
    const response = await get(`${apiBindings.api}getMaps`, undefined);
    if (!response.success || !Array.isArray(response.data)) return undefined;
    return response.data as MapManifestEntry[];
}

// What players actually have. Editing and publishing both happen against the
// dev backend, so without this the editor can't tell an unpromoted map from a
// promoted one — which is how maps came to be published and never appear in the
// live client. Returns undefined on failure, which callers must treat as
// "unknown", never as "nothing is live" — a backend older than getLiveMaps 404s
// here, and that must not read as an empty live list.
export async function fetchLiveMapList(): Promise<MapManifestEntry[] | undefined> {
    const response = await get(`${apiBindings.api}getLiveMaps`, undefined);
    if (!response.success || !Array.isArray(response.data)) return undefined;
    return response.data as MapManifestEntry[];
}

export async function fetchMap(name: string, versionNumber?: number): Promise<MapDocument | undefined> {
    const version = versionNumber !== undefined ? `&versionNumber=${versionNumber}` : '';
    const response = await get(`${apiBindings.api}getMap?name=${encodeURIComponent(name)}${version}`, undefined);
    if (!response.success) return undefined;
    return sanitizeMapDocument(response.data);
}

// Publish: the backend stamps versionNumber/updatedAtUtc and returns the new
// pin. On failure returns the backend's message (or a generic one).
export async function publishMap(doc: MapDocument): Promise<{name: string; versionNumber: number} | {error: string}> {
    const response = await post(`${apiBindings.api}setMap`, JSON.stringify(doc), undefined);
    if (!response.success || typeof response.data?.versionNumber !== 'number') {
        return {error: response.data?.message ?? 'Could not publish the map.'};
    }
    return {name: response.data.name, versionNumber: response.data.versionNumber};
}

export async function deleteMapRequest(name: string): Promise<{success: boolean; error?: string}> {
    const response = await deleteRequest(`${apiBindings.api}deleteMap?name=${encodeURIComponent(name)}`, '', undefined);
    if (!response.success) return {success: false, error: response.data?.message ?? `Could not delete map "${name}".`};
    return {success: true};
}

// Push one immutable version to live. Always pass the version being looked at
// rather than letting the backend default to "latest" — otherwise a publish
// between reviewing and promoting would quietly send something else.
export async function promoteMapRequest(name: string, versionNumber: number): Promise<{success: boolean; error?: string}> {
    const response = await post(
        `${apiBindings.api}promoteMap?name=${encodeURIComponent(name)}&versionNumber=${versionNumber}`, '', undefined);
    if (!response.success) return {success: false, error: response.data?.message ?? `Could not promote map "${name}".`};
    return {success: true};
}

// Withdraw from live. Tombstones the live manifest entry; version blobs stay, so
// any game that pinned this map keeps loading it.
export async function demoteMapRequest(name: string): Promise<{success: boolean; error?: string}> {
    const response = await post(`${apiBindings.api}demoteMap?name=${encodeURIComponent(name)}`, '', undefined);
    if (!response.success) return {success: false, error: response.data?.message ?? `Could not withdraw map "${name}" from live.`};
    return {success: true};
}
